// Left-rail data-provenance panel: when each sync feed last ran, whether it
// succeeded, and how far its data reaches. Answers "is this live?" without
// leaving the page. Refreshes every 5 minutes while the app is open.
//
// A failing feed has to SAY so. lastRunAt is stamped on failed runs too, and
// this panel used to print it as "synced 3h ago" whatever the status — the
// financials sync failed on every run from 2026-10-13 and read as freshly
// synced for two days. A failed (or structurally broken) feed now reads
// "Sync failing since …" with the figures' real reach underneath; the date
// is the one the sync recorded, and is left off when it recorded none.

import { useEffect, useState } from 'react'
import { useApiToken } from '@/auth/useApiToken'
import { api } from '@/api/client'
import type { FreshnessFeed } from '@/api/client'

const FEED_LABELS: Record<string, string> = {
  'relationship-graph': 'Accounts & properties',
  'kpis-daily': 'Occupancy & revenue',
  financials: 'Financials (Yardi GL)',
}

function relTime(iso: string | null): string {
  if (!iso) return 'never'
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.round(mins / 60)
  if (hrs < 48) return `${hrs}h ago`
  return `${Math.round(hrs / 24)}d ago`
}

// dataThrough is a SQL `date` that arrives as UTC midnight — format it in UTC,
// or a Pacific browser shows the day (and for financials the MONTH) before.
function throughLabel(feed: FreshnessFeed): string | null {
  if (!feed.dataThrough) return null
  const d = new Date(feed.dataThrough)
  if (feed.syncType === 'financials') {
    return `through ${d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' })}`
  }
  return `through ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}`
}

type FeedState = 'ok' | 'partial' | 'failing'

function feedState(feed: FreshnessFeed): FeedState {
  if (feed.lastStatus === 'FAILED' || feed.structurallyBroken) return 'failing'
  if (feed.lastStatus === 'SUCCESS') return 'ok'
  return 'partial' // PARTIAL, or no completed run yet
}

/** The line(s) under the feed name, plus the full sentence for the tooltip. */
function describe(feed: FreshnessFeed, label: string) {
  const through = throughLabel(feed)
  const state = feedState(feed)
  if (state === 'failing') {
    const since = feed.failingSince ? new Date(feed.failingSince) : null
    const alert = since
      ? `Sync failing since ${since.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
      : 'Sync failing'
    const figures = through ? `figures ${through}` : 'no figures yet'
    return {
      state,
      alert,
      meta: `${figures} · tried ${relTime(feed.lastRunAt)}`,
      title:
        `${label}: sync failing` +
        (since ? ` since ${since.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}` : '') +
        ` — ${through ? `figures are as of ${through.replace(/^through /, '')}` : 'no figures synced yet'}.` +
        ` Last attempt ${relTime(feed.lastRunAt)}.`,
    }
  }
  const verb = feed.lastStatus === 'PARTIAL' ? 'partly synced' : 'synced'
  return {
    state,
    alert: null,
    meta: `${through ? `${through} · ` : ''}${verb} ${relTime(feed.lastRunAt)}`,
    title: `${feed.syncType}: ${feed.lastStatus ?? 'no run yet'}`,
  }
}

export function DataFreshness() {
  const getToken = useApiToken()
  const [feeds, setFeeds] = useState<FreshnessFeed[] | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const token = await getToken()
        const r = await api.getFreshness(token)
        if (!cancelled) setFeeds(r.feeds)
      } catch {
        /* panel is best-effort chrome — leave the previous reading */
      }
    }
    void load()
    const timer = setInterval(load, 5 * 60_000)
    return () => { cancelled = true; clearInterval(timer) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!feeds?.length) return null
  return (
    <div className="rail-fresh">
      <div className="rail-section-label label">Data freshness</div>
      {feeds.map((f) => {
        const label = FEED_LABELS[f.syncType] ?? f.syncType
        const d = describe(f, label)
        const dot = d.state === 'ok' ? 'ok' : d.state === 'failing' ? 'bad' : 'warn'
        return (
          <div key={f.syncType} className="rail-fresh-row" title={d.title}>
            <span className={`rail-fresh-dot ${dot}`} />
            <div className="rail-fresh-body">
              <span className="rail-fresh-name">{label}</span>
              {d.alert && <span className="rail-fresh-alert">{d.alert}</span>}
              <span className="rail-fresh-meta">{d.meta}</span>
            </div>
          </div>
        )
      })}
    </div>
  )
}
